import { z } from "zod";

/**
 * The presigned staging upload, shared by the flows that use it.
 *
 * Bytes never pass through a handler: the caller asks for a URL, PUTs the file
 * straight to blob storage, and hands the staging key back to be claimed. The
 * two schemas here are the first half of that exchange, which is identical
 * wherever it is used — what differs is only what the claim then does with the
 * file.
 */

/**
 * Step 1 of the presigned upload. The declared content type and size are
 * checked before a byte moves, so an unacceptable file is refused when the user
 * picks it rather than after they have waited for the transfer.
 *
 * Both values are the caller's *claim*. A presigned PUT is not bound to either,
 * so the claim step must measure the stored object rather than trust these.
 */
export const PresignedUploadUrlInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1),
  fileSizeBytes: z.number().int().positive(),
});
export type PresignedUploadUrlInput = z.infer<
  typeof PresignedUploadUrlInputSchema
>;

export const PresignedUploadUrlResponseSchema = z.object({
  /** Opaque staging key, handed back verbatim when claiming the upload. */
  uploadKey: z.string(),
  uploadUrl: z.string().url(),
});
export type PresignedUploadUrlResponse = z.infer<
  typeof PresignedUploadUrlResponseSchema
>;
